import type { EventRollupInput } from "@/features/DragonTiles/status/rollup";
import type {
  DragonTile,
  DragonTileAttentionEntities,
  InnerTile,
  InnerTileId,
  TileStatus,
} from "@/types/dragon-tiles";

import {
  buildDragonTileReport,
  isBroadcastDragonTile,
  toDragonTileRollupInput,
  toTileStatus,
} from "@/features/DragonTiles/status/rollup";
import { describeLayerReport } from "@/features/DragonTiles/status/statusCopy";

/**
 * Returns the parent dragon tile that owns `innerTileId`, or undefined if not found.
 */
export function findParentDragonTileForInnerTileId(
  dragonTiles: DragonTile[] | undefined,
  innerTileId: InnerTileId | undefined,
): DragonTile | undefined {
  if (!dragonTiles?.length || !innerTileId) return undefined;
  const id = String(innerTileId).trim();
  if (!id) return undefined;
  return dragonTiles.find((tile) =>
    tile.inner_tiles?.some((inner) => inner.id === id),
  );
}

export interface TileStatusRollup {
  framesLoaded: boolean;
  events?: EventRollupInput[];
}

export interface TileAttention {
  status: TileStatus;
  message: string | undefined;
}

export function getTileAttention(
  tile: DragonTile,
  isComingSoon: boolean,
  rollup: TileStatusRollup,
): TileAttention {
  if (isComingSoon) return { status: "Off", message: undefined };
  if (isBroadcastDragonTile(tile) && tile.is_active) {
    return { status: "Running", message: undefined };
  }
  const report = buildDragonTileReport(
    toDragonTileRollupInput(tile, {
      framesLoaded: rollup.framesLoaded,
      events: rollup.events,
    }),
  );
  if (report !== null) {
    return {
      status: toTileStatus(report.status),
      message: describeLayerReport(report),
    };
  }
  if (tile.needs_attention.length > 0) {
    return {
      status: "Needs Attention",
      message:
        getNeedsAttentionMessage(tile) ?? "This Dragon Tile needs attention.",
    };
  }
  return { status: tile.is_active ? "Active" : "Off", message: undefined };
}

export function getTileStatus(
  tile: DragonTile,
  isComingSoon: boolean,
  rollup: TileStatusRollup,
): TileStatus {
  return getTileAttention(tile, isComingSoon, rollup).status;
}

function formatEntityList(entities: DragonTileAttentionEntities): string {
  const extra = entities.total - entities.names.length;
  const list = entities.names.join(", ");
  return extra > 0 ? `${list} and ${extra} more` : list;
}

function audienceClause(entities: DragonTileAttentionEntities | null): {
  phrase: string;
  is: "is" | "are";
  has: "has" | "have";
} {
  if (!entities || entities.total === 0 || entities.names.length === 0) {
    return { phrase: "its audiences", is: "are", has: "have" };
  }
  if (entities.all && entities.total > 3) {
    return { phrase: "all of its audiences", is: "are", has: "have" };
  }
  const plural = Math.max(entities.total, entities.names.length) !== 1;
  return {
    phrase: `${plural ? "audiences" : "audience"} ${formatEntityList(entities)}`,
    is: plural ? "are" : "is",
    has: plural ? "have" : "has",
  };
}

export function getNeedsAttentionMessage(tile: DragonTile): string | undefined {
  const detail = tile.needs_attention[0];
  if (!detail) return undefined;
  switch (detail.reason) {
    case "DRAGON_TILE_ATTENTION_REASON_ACTIVE_NO_INNER_TILES":
      return "This Dragon Tile is turned on but has nothing to send yet.";
    case "DRAGON_TILE_ATTENTION_REASON_ACTIVE_ALL_AUDIENCES_INACTIVE": {
      const { phrase, is } = audienceClause(detail.inactive_audiences);
      return `This Dragon Tile is turned on but ${phrase} ${is} off.`;
    }
    case "DRAGON_TILE_ATTENTION_REASON_ACTIVE_NO_ACTIVE_FRAMES": {
      const frames = detail.inactive_frames;
      if (frames && frames.total > 0) {
        const list =
          frames.names.length > 0 ? ` (${formatEntityList(frames)})` : "";
        return `This Dragon Tile is turned on but none of its tiles are active${list}.`;
      }
      const { phrase, has } = audienceClause(detail.active_audiences);
      return `This Dragon Tile is turned on but ${phrase} ${has} no tiles to send.`;
    }
    case "DRAGON_TILE_ATTENTION_REASON_INACTIVE_HAS_SENDABLE_AUDIENCES": {
      const { phrase, has } = audienceClause(detail.active_audiences);
      const frames = detail.active_frames;
      const frameList =
        frames && frames.total > 0 && frames.names.length > 0
          ? ` (${formatEntityList(frames)})`
          : "";
      return `This Dragon Tile is turned off but ${phrase} ${has} active tiles${frameList} — nothing will send while the Dragon Tile is off.`;
    }
    default:
      return undefined;
  }
}

/**
 * Extracts all tile IDs from an array of inner tiles
 * @param innerTiles - Array of inner tiles to extract tile IDs from
 * @returns Array of tile IDs (strings)
 */
export const getAllInnerTileFrameIds = (innerTiles: InnerTile[]): string[] => {
  return innerTiles.reduce<string[]>((acc, innerTile) => {
    if (Array.isArray(innerTile.frames)) {
      innerTile.frames.forEach((frameRef) => {
        if (frameRef && typeof frameRef.id === "string") {
          acc.push(frameRef.id);
        }
      });
    }
    return acc;
  }, []);
};

/**
 * Extracts all tile IDs from all dragon tiles
 * @param dragonTiles - Array of dragon tiles to extract tile IDs from
 * @returns Array of tile IDs (strings)
 */
export const getAllDragonTilesFrameIds = (
  dragonTiles: DragonTile[],
): string[] => {
  return dragonTiles.reduce<string[]>((acc, dragonTile) => {
    const frameIds = getAllInnerTileFrameIds(dragonTile.inner_tiles);
    acc.push(...frameIds);
    return acc;
  }, []);
};
